import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Settings, BookOpen, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TeamForm, EMPTY_TEAM } from "@/components/TeamForm";

export function CreateTeamDialog({ open, onOpenChange }) {
  const { loadTeams, setActiveTeam } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY_TEAM);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/teams", form);
      await loadTeams();
      setActiveTeam(data.id);
      toast.success(`${data.name} created with 3 example plays`);
      onOpenChange(false);
      setForm(EMPTY_TEAM);
      nav(`/teams/${data.id}/playbook`);
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111D35] border-[#1F3358] text-white max-h-[92vh] overflow-y-auto" data-testid="create-team-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">New team</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <TeamForm value={form} onChange={setForm} />
          <button className="btn btn-primary w-full" disabled={busy || !form.name.trim()} data-testid="create-team-submit">{busy ? "Creating…" : "Create team"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TeamCard({ team, active, onActivate }) {
  return (
    <div className={`card p-5 tpc-rise ${active ? "ring-2 ring-[#FF6B00]" : ""}`} data-testid={`team-card-${team.id}`}>
      <div className="flex items-start gap-3">
        <span className="w-12 h-12 rounded-xl border-2 border-white/80 shrink-0" style={{ background: `linear-gradient(135deg, ${team.colors.primary} 50%, ${team.colors.secondary} 50%)` }} aria-hidden="true" />
        <div className="min-w-0">
          <h3 className="font-display text-2xl font-bold uppercase truncate">{team.name}</h3>
          <p className="text-sm text-slate-400">{[team.age_group, team.format === "flag" ? "Flag" : "Tackle", `${team.player_count} per side`].filter(Boolean).join(" · ")}</p>
          <p className="text-xs text-slate-500 mt-1">{team.play_count ?? 0} plays{team.role === "coach" && <span className="ml-2 font-bold text-[#FACC15]" data-testid="team-role-assistant">Assistant coach</span>}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        <Link to={`/teams/${team.id}/playbook`} onClick={onActivate} className="btn btn-primary flex-1" data-testid={`team-open-playbook-${team.id}`}><BookOpen size={18} />Playbook</Link>
        <Link to={`/teams/${team.id}/settings`} className="btn btn-ghost" data-testid={`team-settings-${team.id}`}><Settings size={18} />{team.can_manage ? "Settings" : "Coaches"}</Link>
        {active ? <span className="btn text-[#FF8A1F]" data-testid="team-active-badge"><Check size={18} />Active</span> : (
          <button onClick={onActivate} className="btn btn-ghost" data-testid={`team-activate-${team.id}`}>Make active</button>
        )}
      </div>
    </div>
  );
}

export default function Teams() {
  const { teams, activeTeam, setActiveTeam } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div><p className="label">Your teams</p><h1 className="h1">Teams</h1></div>
        <button className="btn btn-primary" onClick={() => setOpen(true)} data-testid="btn-create-team"><Plus size={18} />New team</button>
      </div>
      {!teams.length && (
        <div className="card p-8 text-center" data-testid="teams-empty">
          <p className="h2">No teams yet</p>
          <p className="text-slate-400 mt-2">Create your first team. We'll add 3 example plays you can change.</p>
          <button className="btn btn-primary mt-5" onClick={() => setOpen(true)} data-testid="btn-create-first-team"><Plus size={18} />Create my team</button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map((t) => <TeamCard key={t.id} team={t} active={activeTeam?.id === t.id} onActivate={() => setActiveTeam(t.id)} />)}
      </div>
      <CreateTeamDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
